import api from "../config/api";
import type { ApiResponse } from "../types";

// =====================================================
// 📝 Public Menu Types
// =====================================================

export interface PublicPromotion {
  _id: string;
  title: string;
  description: string;
  discountPercentage: number;
  discountAmount?: {
    LAK: number;
    THB: number;
    USD: number;
  };
  discountType: 'percentage' | 'fixed';
  badge: string;
  image?: string;
  startDate: string;
  endDate: string;
  terms?: string;
}

export interface PublicPackage {
  _id: string;
  courseName: string;
  categoryName: string;
  description?: string;
  sessions: number;
  image?: string;
  validityDays: number;
  prices: {
    LAK: number;
    THB: number;
    USD: number;
  };
}

export interface PublicService {
  _id: string;
  name: string;
  description?: string;
  duration?: string;
  image?: string;
  prices: {
    LAK: number;
    THB: number;
    USD: number;
  };
  packages: Array<{
    sessions: number;
    validityDays: number;
    price: {
      LAK: number;
      THB: number;
      USD: number;
    };
  }>;
}

export interface PublicCategory {
  _id: string;
  name: string;
  description?: string;
  icon?: string;
  services: PublicService[];
}

export interface PublicCourseDetail {
  _id: string;
  name: string;
  description?: string;
  category: {
    _id: string;
    name: string;
  };
  duration?: string;
  prices: {
    LAK: number;
    THB: number;
    USD: number;
  };
  packages?: Array<{
    sessions: number;
    validityDays: number;
    prices: {
      LAK: number;
      THB: number;
      USD: number;
    };
  }>;
}

export interface CompleteMenuData {
  promotions: PublicPromotion[];
  packages: PublicPackage[];
  menu: PublicCategory[];
}

// =====================================================
// 📝 Public Menu Service
// =====================================================

export const publicMenuService = {
  // ດຶງໂປຣໂມຊັ່ນທີ່ກຳລັງໃຊ້ງານ (Public)
  getPromotions: async (): Promise<ApiResponse<PublicPromotion[]>> => {
    const response = await api.get('/public/promotions');
    return response.data;
  },

  // ດຶງແພັກເກັດທັງໝົດ (Public)
  getPackages: async (): Promise<ApiResponse<PublicPackage[]>> => {
    const response = await api.get('/public/packages');
    return response.data;
  },

  // ດຶງເມນູທັງໝົດ (Public)
  getMenu: async (): Promise<ApiResponse<PublicCategory[]>> => {
    const response = await api.get('/public/menu');
    return response.data;
  },

  // ດຶງລາຍລະອຽດຄອສ (Public)
  getCourseDetail: async (id: string): Promise<ApiResponse<PublicCourseDetail>> => {
    const response = await api.get(`/public/courses/${id}`);
    return response.data;
  },

  // ດຶງຂໍ້ມູນທັງໝົດພ້ອມກັນ (ແນະນຳ)
  getCompleteMenu: async (): Promise<ApiResponse<CompleteMenuData>> => {
    const response = await api.get('/public/complete-menu');
    return response.data;
  },
};

// =====================================================
// 📝 Export types
// =====================================================

export type {
  ApiResponse,
};